/**
 * 조명 dry-run 시뮬레이터 — manage-backend 프록시를 통해 lights 서비스의
 * dry-run 캡처 모드를 제어하고 fan-out 결과를 실시간으로 관찰한다.
 *
 * 흐름:
 *   dry-run ON → 트리거(모션/직접제어) → getEvents 폴링 →
 *   LightingStatus 스토어 업데이트 → 맵 CCTV 하이라이트 + 이벤트 패널 표시.
 *
 * 직접 제어는 선택 CCTV 또는 **전체 조명**(현재 맵의 모든 CCTV)을 대상으로,
 * 자동/수동(manual=자동 디밍을 덮어써 값 유지) 모드를 선택해 보낼 수 있다.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  DeleteOutline as ClearIcon,
  PlaylistPlay as AutoRunIcon,
} from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { LightsSimulatorService, type SimEvent } from '../../services/LightsSimulatorService';
import { useLightsCaptureSession } from './hooks/useLightsCaptureSession';
import { useSimInteractionStore } from './model/simInteractionStore';
import type { CctvLightLink, MapCctv } from './mapAssetFix';
import type { SimulatorCctv } from '../../models/Simulator';
import { sleep, waitForLightEvent, type AutoCheckResult, type SimAutoRunHandle } from './autoTestUtils';
import AutoTestReportDialog from './AutoTestReportDialog';

interface LightsSimulatorProps {
  /** 현재 맵의 CCTV 목록 — SimulatorPage 에서 전달 */
  cctvs: SimulatorCctv[];
  /** 맵 graphJson 에서 추출한 실제 배치 CCTV 이름 — 전체 대상/선택지 보강 */
  mapCctvNames?: string[];
  /** 맵 CCTV 이름+IP+종류 — 자동 실행에서 단방향(경광등) CCTV 판별에 사용 */
  mapCctvList?: MapCctv[];
  /** CCTV↔조명 관계 — 자동 실행에서 "연결된 조명 있음" 여부 확인에 사용 */
  cctvLightLinks?: CctvLightLink[];
  /** 현재 선택된 맵 id (미사용이지만 향후 확장용) */
  mapId: number | null;
}

const LightsSimulator = forwardRef<SimAutoRunHandle, LightsSimulatorProps>(({
  cctvs,
  mapCctvNames = [],
  mapCctvList = [],
  cctvLightLinks = [],
}, ref) => {
  // ── dry-run 캡처 세션 (공유 훅) ───────────────────────────────
  const {
    dryRunEnabled,
    togglingMode,
    modeError,
    toggleMode,
    events,
    clearEvents,
  } = useLightsCaptureSession();

  // ── 트리거 상태 ───────────────────────────────────────────────
  const [selectedCctv, setSelectedCctv] = useState<string>('');
  // 선택된 CCTV 를 맵에서 하이라이트 + 센터링(모든 시뮬레이터 탭 공통 채널).
  // onChange 가 아니라 선택 "값"을 감시하는 effect 로 게시한다 — 탭 진입 시의
  // 자동 첫 선택, 이미 선택된 항목 재선택(onChange 미발화)까지 모두 맵에 반영.
  const setMapFocus = useSimInteractionStore(s => s.setMapFocus);
  useEffect(() => {
    if (selectedCctv) setMapFocus({ kind: 'cctv', name: selectedCctv });
  }, [selectedCctv, setMapFocus]);
  const [motionDir, setMotionDir] = useState<'left' | 'right'>('left');
  const [controlType, setControlType] = useState<'dimming' | 'warnLamp' | 'parking'>('dimming');
  // 통로등 점등 목표는 100% 가 아니라 80%(기본 디밍 20 → 페이드 80). 현실적 기본값.
  const [controlValue, setControlValue] = useState<number>(80);
  // 직접 제어 대상: 선택 CCTV 한 대 vs 전체 조명(맵의 모든 CCTV).
  const [controlTarget, setControlTarget] = useState<'selected' | 'all'>('selected');
  // 디밍 우선순위: auto(자동 로직에 양보) vs manual(자동을 덮어써 값 유지).
  const [controlMode, setControlMode] = useState<'auto' | 'manual'>('auto');
  const [triggerError, setTriggerError] = useState<string | null>(null);
  // "200 OK 인데 캡처 0건"(연결된 조명 모듈 없음 등) 을 알리는 경고. 에러와 구분.
  const [triggerWarn, setTriggerWarn] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);

  // ── CCTV 목록(맵 graphJson + 시뮬레이터 API 합집합, 중복 제거) ──
  const allCctvNames = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const n of [...mapCctvNames, ...cctvs.map(c => c.name)]) {
      if (n && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }
    return out;
  }, [mapCctvNames, cctvs]);

  useEffect(() => {
    if (allCctvNames.length > 0 && !selectedCctv) {
      setSelectedCctv(allCctvNames[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCctvNames]);

  // ── dry-run 토글 ─────────────────────────────────────────────
  const handleToggleMode = () => {
    void toggleMode();
  };

  // ── 트리거 ───────────────────────────────────────────────────
  const handleMotion = async () => {
    setTriggerError(null);
    setTriggerWarn(null);
    setTriggering(true);
    try {
      await LightsSimulatorService.triggerMotion(selectedCctv, motionDir);
    } catch (e) {
      setTriggerError(e instanceof Error ? e.message : '모션 트리거 실패');
    } finally {
      setTriggering(false);
    }
  };

  const handleControl = async () => {
    setTriggerError(null);
    setTriggerWarn(null);
    setTriggering(true);
    const targets = controlTarget === 'all' ? allCctvNames : [selectedCctv].filter(Boolean);
    try {
      if (targets.length === 0) {
        setTriggerError('제어할 CCTV 가 없습니다.');
        return;
      }
      const r = await LightsSimulatorService.triggerControl(
        targets,
        controlType,
        controlValue,
        controlMode,
      );
      if (r.errors.length > 0) {
        setTriggerError(`조명 서버 오류: ${r.errors.join('; ')}`);
      } else if (r.captured === 0) {
        // 200 OK 지만 캡처된 명령 0건 — 맵에 표시되지 않는 가장 흔한 원인.
        setTriggerWarn(
          `요청은 전송됐지만(대상 ${targets.length}) 제어된 조명이 0건입니다 — 연결된 조명 모듈이 없거나(매핑 미등록), 이미 목표값이거나, 선택한 종류와 모듈 타입이 다릅니다.`,
        );
      }
    } catch (e) {
      setTriggerError(e instanceof Error ? e.message : '직접제어 트리거 실패');
    } finally {
      setTriggering(false);
    }
  };

  const handleClearEvents = () => {
    clearEvents();
  };

  // CCTV 이름 → 연결 조명 셀 id (EmergencyBellSimulator 와 동일 소스/패턴).
  const lightIdsByCctv = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const link of cctvLightLinks) m.set(link.cctvName, link.lightIds);
    return m;
  }, [cctvLightLinks]);

  // CCTV 이름 → 종류(단방향 여부). 단방향(경광등) CCTV 는 통로등이 아니라
  // 경광등/충돌방지등이 켜져야 하므로 자동 실행 판정 기준이 다르다.
  const kindByCctv = useMemo(() => {
    const m = new Map<string, MapCctv['kind']>();
    for (const c of mapCctvList) m.set(c.name, c.kind);
    return m;
  }, [mapCctvList]);

  // ── 자동 실행: CCTV 를 하나씩 맵에서 보여주며 좌/우 모션을 트리거하고,
  // 각 방향마다 조명이 실제로 켜지는(캡처 이벤트 on=true) 지, 애초에 연결된
  // 조명이 있는지 확인한다. ──
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoReport, setAutoReport] = useState<AutoCheckResult[] | null>(null);
  const [showReport, setShowReport] = useState(false);
  const stopRequestedRef = useRef(false);
  // CCTV 사이 이동/대기 간격(초) — 기존 하드코딩값(0.6초)을 기본값으로 유지.
  const [autoRunIntervalSec, setAutoRunIntervalSec] = useState(0.6);

  // 판정 루프만 순수 추출(로컬 report/showReport state는 안 건드림) — 개별
  // "자동 실행" 버튼과 통합 자동 테스트(SimulatorPage index.tsx)가 공유한다.
  const runCheckLoop = useCallback(async (): Promise<AutoCheckResult[]> => {
    if (!dryRunEnabled) await toggleMode();
    const seek = await LightsSimulatorService.getEvents(0);
    let cursor = seek.cursor;
    const results: AutoCheckResult[] = [];
    for (const name of allCctvNames) {
        if (stopRequestedRef.current) break;
        setSelectedCctv(name);
        setMapFocus({ kind: 'cctv', name });
        await sleep(autoRunIntervalSec * 1000);

        // 단방향(경광등) CCTV 는 통로등이 아니라 경광등/충돌방지등이 켜져야
        // 방향 트리거가 제대로 동작한 것이다 — "아무 조명이나 켜지면 통과"로
        // 판정하면 통로등만 켜지는(혹은 아무것도 안 켜지는) 경우까지 통과로
        // 잘못 표시될 수 있어 lightType 까지 확인한다.
        const isWarningCctv = kindByCctv.get(name) === 'warningCctv';
        const eventMatches = (ev: SimEvent) =>
          ev.cctvName === name &&
          ev.on &&
          (!isWarningCctv || ev.lightType === 'warning' || ev.lightType === 'caution');

        let leftOk = false;
        let rightOk = false;
        try {
          await LightsSimulatorService.triggerMotion(name, 'left');
          const l = await waitForLightEvent(cursor, eventMatches);
          cursor = l.cursor;
          leftOk = !!l.hit;
        } catch {
          // leftOk 는 false 유지
        }
        try {
          await LightsSimulatorService.triggerMotion(name, 'right');
          const r = await waitForLightEvent(cursor, eventMatches);
          cursor = r.cursor;
          rightOk = !!r.hit;
        } catch {
          // rightOk 는 false 유지
        }

        const hasLightLink = (lightIdsByCctv.get(name) ?? []).length > 0;
        const passed = leftOk && rightOk && hasLightLink;
        const failDetail = isWarningCctv
          ? `경광등/충돌방지등 점등 미확인 (left: ${leftOk ? 'OK' : '실패'} / right: ${rightOk ? 'OK' : '실패'}) — 좌우 연동 설정을 확인하세요`
          : `조명 점등 미확인 (left: ${leftOk ? 'OK' : '실패'} / right: ${rightOk ? 'OK' : '실패'})`;
        const detail = !hasLightLink
          ? '연결된 조명이 없습니다.'
          : passed
            ? '좌/우 모션 이동 모두 조명 점등 확인'
            : failDetail;
        results.push({ name, passed, detail });
        setAutoReport([...results]);
    }
    return results;
  }, [dryRunEnabled, toggleMode, allCctvNames, kindByCctv, lightIdsByCctv, autoRunIntervalSec, setMapFocus]);

  const handleAutoRun = async () => {
    if (allCctvNames.length === 0 || autoRunning) return;
    setAutoRunning(true);
    setAutoReport(null);
    stopRequestedRef.current = false;
    try {
      await runCheckLoop();
      setShowReport(true);
    } finally {
      setAutoRunning(false);
    }
  };

  const handleStopAutoRun = () => {
    stopRequestedRef.current = true;
  };

  useImperativeHandle(ref, () => ({
    runAutoCheck: async () => {
      if (autoRunning) return [];
      setAutoRunning(true);
      setAutoReport(null);
      stopRequestedRef.current = false;
      try {
        const results = await runCheckLoop();
        setAutoReport(results);
        return results;
      } finally {
        setAutoRunning(false);
      }
    },
    stopAutoCheck: handleStopAutoRun,
  }), [runCheckLoop, autoRunning]);

  const motionDisabled = triggering || !dryRunEnabled || !selectedCctv;
  // 전체 제어는 selectedCctv 없이도 가능(전체 대상). 선택 제어만 selectedCctv 필요.
  const controlDisabled =
    triggering || !dryRunEnabled || (controlTarget === 'selected' && !selectedCctv);

  return (
    <Stack spacing={2}>
      {/* ── 1. dry-run 토글 ── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="subtitle2" sx={{ flex: '1 1 auto', minWidth: 0 }}>
            Dry-run 캡처 모드
          </Typography>
          <Chip
            size="small"
            label={dryRunEnabled ? '활성' : '비활성'}
            color={dryRunEnabled ? 'success' : 'default'}
            sx={{ flexShrink: 0 }}
          />
          <Button
            variant={dryRunEnabled ? 'outlined' : 'contained'}
            color={dryRunEnabled ? 'error' : 'primary'}
            size="small"
            startIcon={dryRunEnabled ? <StopIcon /> : <PlayIcon />}
            onClick={handleToggleMode}
            disabled={togglingMode}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {togglingMode ? '전환 중...' : dryRunEnabled ? 'OFF' : 'ON'}
          </Button>
        </Stack>
        {modeError && (
          <Alert severity={modeError.includes('허용') || modeError.includes('403') ? 'warning' : 'error'} sx={{ mt: 1 }}>
            {modeError}
          </Alert>
        )}
        {!dryRunEnabled && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            ON 으로 전환하면 이후 트리거 명령의 fan-out 결과를 캡처해 맵에 표시합니다.
            하드웨어로는 전송되지 않습니다.
          </Typography>
        )}
      </Paper>

      {/* ── 2. CCTV 선택 + 트리거 ── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="subtitle2">트리거</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              type="number"
              label="간격(초)"
              value={autoRunIntervalSec}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v)) setAutoRunIntervalSec(Math.min(5, Math.max(0.1, v)));
              }}
              disabled={autoRunning}
              inputProps={{ step: 0.1, min: 0.1, max: 5 }}
              sx={{ width: 100 }}
            />
            {autoRunning ? (
              <Button size="small" variant="outlined" color="error" onClick={handleStopAutoRun}>
                정지
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoRunIcon />}
                onClick={handleAutoRun}
                disabled={allCctvNames.length === 0}
              >
                자동 실행
              </Button>
            )}
          </Stack>
        </Stack>

        {/* CCTV picker */}
        {allCctvNames.length > 0 ? (
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel id="ls-cctv-select-label">CCTV</InputLabel>
            <Select<string>
              labelId="ls-cctv-select-label"
              label="CCTV"
              value={selectedCctv}
              onChange={(e: SelectChangeEvent<string>) => setSelectedCctv(e.target.value)}
              disabled={!dryRunEnabled}
            >
              {allCctvNames.map(name => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            label="CCTV 이름"
            placeholder="P1_B1_7_1"
            value={selectedCctv}
            onChange={e => setSelectedCctv(e.target.value)}
            size="small"
            fullWidth
            disabled={!dryRunEnabled}
            helperText="lights 서비스에 등록된 CCTV 이름 (맵에 CCTV 없음)"
            sx={{ mb: 2 }}
          />
        )}

        {triggerError && <Alert severity="error" sx={{ mb: 1 }}>{triggerError}</Alert>}
        {triggerWarn && <Alert severity="warning" sx={{ mb: 1 }}>{triggerWarn}</Alert>}

        <Stack spacing={1.5}>
          {/* 모션 */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              모션 감지
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 1 }}
            >
              <ToggleButtonGroup
                size="small"
                value={motionDir}
                exclusive
                onChange={(_e, v) => { if (v) setMotionDir(v as 'left' | 'right'); }}
                disabled={motionDisabled}
              >
                <ToggleButton value="left">Left</ToggleButton>
                <ToggleButton value="right">Right</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                size="small"
                onClick={handleMotion}
                disabled={motionDisabled}
                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                트리거
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* 직접 제어 (선택/전체 · 자동/수동) */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              직접 제어
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 0.5, mb: 1, flexWrap: 'wrap', rowGap: 1 }}
            >
              <ToggleButtonGroup
                size="small"
                value={controlTarget}
                exclusive
                onChange={(_e, v) => { if (v) setControlTarget(v as 'selected' | 'all'); }}
                disabled={triggering || !dryRunEnabled}
              >
                <ToggleButton value="selected">선택 CCTV</ToggleButton>
                <ToggleButton value="all">전체{allCctvNames.length ? ` (${allCctvNames.length})` : ''}</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                size="small"
                value={controlMode}
                exclusive
                onChange={(_e, v) => { if (v) setControlMode(v as 'auto' | 'manual'); }}
                disabled={triggering || !dryRunEnabled}
              >
                <ToggleButton value="auto">자동</ToggleButton>
                <ToggleButton value="manual">수동</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexWrap: 'wrap', rowGap: 1 }}
            >
              <FormControl size="small" sx={{ flex: '1 1 110px', minWidth: 100 }}>
                <InputLabel id="ls-ctrl-type-label">종류</InputLabel>
                <Select<'dimming' | 'warnLamp' | 'parking'>
                  labelId="ls-ctrl-type-label"
                  label="종류"
                  value={controlType}
                  onChange={(e: SelectChangeEvent<'dimming' | 'warnLamp' | 'parking'>) =>
                    setControlType(e.target.value as 'dimming' | 'warnLamp' | 'parking')
                  }
                  disabled={controlDisabled}
                >
                  <MenuItem value="dimming">통로등</MenuItem>
                  <MenuItem value="warnLamp">경광등</MenuItem>
                  <MenuItem value="parking">만공차등</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="값 (0=off, 1-100)"
                type="number"
                size="small"
                value={controlValue}
                onChange={e => setControlValue(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                inputProps={{ min: 0, max: 100 }}
                disabled={controlDisabled}
                sx={{ flex: '1 1 130px', minWidth: 120 }}
              />
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={handleControl}
                disabled={controlDisabled}
                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                전송
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {controlTarget === 'all'
                ? '전체: 맵의 모든 CCTV 조명을 한 번에 제어합니다.'
                : '선택: 위에서 고른 CCTV 한 대만 제어합니다.'}
              {controlMode === 'manual' && ' · 수동은 자동 디밍을 덮어써 값을 유지합니다.'}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* ── 3. 라이브 fan-out 패널 ── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="subtitle2" sx={{ flex: '1 1 auto', minWidth: 0 }}>
            Fan-out 이벤트
            {dryRunEnabled && (
              <Chip size="small" label="폴링 중" color="success" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<ClearIcon />}
            onClick={handleClearEvents}
            disabled={events.length === 0}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            초기화
          </Button>
        </Stack>
        {events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {dryRunEnabled
              ? '트리거를 실행하면 캡처된 이벤트가 여기 표시됩니다.'
              : 'Dry-run 을 ON 하고 트리거를 실행하세요.'}
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ maxHeight: 260, overflow: 'auto' }}>
            {events.map(ev => (
              <Box
                key={ev.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  py: 0.25,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ width: 72, flexShrink: 0 }}>
                  {ev.at}
                </Typography>
                <Chip
                  size="small"
                  label={ev.cctvName}
                  color="primary"
                  variant="outlined"
                  sx={{ maxWidth: 160 }}
                />
                <Chip
                  size="small"
                  label={ev.lightType}
                  color="default"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={ev.on ? `${ev.value}%` : 'OFF'}
                  color={ev.on ? 'warning' : 'default'}
                />
                <Typography variant="caption" color="text.secondary">
                  {ev.mode}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <AutoTestReportDialog
        open={showReport}
        title="조명 체크 — 자동 실행 리포트"
        results={autoReport ?? []}
        onClose={() => setShowReport(false)}
      />
    </Stack>
  );
});

LightsSimulator.displayName = 'LightsSimulator';

export default LightsSimulator;
